import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, Clock, HeartPulse, Trash2, Undo2 } from "lucide-react";

const dateFr = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Paris" }) : "—";
const heureFr = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }).replace(":", "h") : "—";

export default function BilansSuivi() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/prevention/bilan-sessions").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const complete = (id) => api.post(`/prevention/bilan-sessions/${id}/complete`).then(() => { toast.success("Bilan marqué comme effectué."); load(); }).catch(() => toast.error("Erreur."));
  const reopen = (id) => api.post(`/prevention/bilan-sessions/${id}/reopen`).then(() => { toast.success("Bilan remis en « commencé »."); load(); }).catch(() => toast.error("Erreur."));

  const remove = async (id) => {
    if (!(await confirm({ title: "Supprimer ce suivi", description: "Supprimer définitivement cette ligne de suivi ?", confirmText: "Supprimer", destructive: true }))) return;
    api.delete(`/prevention/bilan-sessions/${id}`).then(() => { toast.success("Suivi supprimé."); load(); }).catch(() => toast.error("Erreur."));
  };
  const clearAll = async () => {
    if (!(await confirm({ title: "Tout supprimer", description: "Vider tout l'historique de suivi des bilans ? Action irréversible.", confirmText: "Tout supprimer", destructive: true }))) return;
    api.post("/prevention/bilan-sessions/purge-all").then((r) => { toast.success(`${r.data.deleted} suivi(s) supprimé(s).`); load(); }).catch(() => toast.error("Erreur."));
  };

  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-muted/20">
      <StaffHeader />
      <main className="mx-auto max-w-3xl px-4 py-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><HeartPulse className="h-6 w-6 text-primary" /> Suivi des bilans</h1>
            <p className="text-sm text-muted-foreground">Suivi anonyme des clients ayant lancé un bilan. Aucune donnée de santé ni identité n'est enregistrée.</p>
          </div>
          <Button variant="outline" size="icon" onClick={load} data-testid="bilans-refresh" className="rounded-xl shrink-0"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>

        <div className="grid grid-cols-2 gap-3 my-4">
          <Card className="rounded-2xl p-4" data-testid="stat-started"><div className="flex items-center gap-2 text-amber-600"><Clock className="h-5 w-5" /><span className="text-sm font-medium">Commencés</span></div><p className="font-heading text-3xl font-bold mt-1">{data?.started ?? 0}</p></Card>
          <Card className="rounded-2xl p-4" data-testid="stat-done"><div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-5 w-5" /><span className="text-sm font-medium">Effectués</span></div><p className="font-heading text-3xl font-bold mt-1">{data?.done ?? 0}</p></Card>
        </div>

        {items.length > 0 && (
          <div className="flex justify-end mb-2">
            <Button variant="ghost" onClick={clearAll} data-testid="bilans-clear-all" className="text-destructive hover:text-destructive gap-1.5 h-9 text-sm"><Trash2 className="h-4 w-4" /> Tout supprimer</Button>
          </div>
        )}

        {loading && !data ? <p className="text-sm text-muted-foreground">Chargement…</p> : items.length === 0 ? (
          <Card className="rounded-2xl p-10 text-center text-muted-foreground" data-testid="bilans-empty">Aucun bilan lancé pour l'instant.</Card>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const done = it.status === "effectue";
              return (
                <Card key={it.id} data-testid={`bilan-row-${it.id}`} className={`rounded-2xl p-4 flex flex-wrap items-center gap-3 ${done ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900" : ""}`}>
                  <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${done ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40"}`}>
                    {done ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold" data-testid={`bilan-status-${it.id}`}>{done ? "Bilan effectué" : "Bilan commencé"}</p>
                    <p className="text-sm text-muted-foreground">
                      Lancé le {dateFr(it.startedAt)} à <b className="text-foreground">{heureFr(it.startedAt)}</b>
                      {done && it.completedAt && <> · effectué à {heureFr(it.completedAt)}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {done ? (
                      <Button variant="outline" onClick={() => reopen(it.id)} data-testid={`bilan-reopen-${it.id}`} className="rounded-xl h-10 gap-1.5 text-sm"><Undo2 className="h-4 w-4" /> Rouvrir</Button>
                    ) : (
                      <Button onClick={() => complete(it.id)} data-testid={`bilan-complete-${it.id}`} className="rounded-xl h-10 gap-1.5 text-sm"><CheckCircle2 className="h-4 w-4" /> Bilan effectué</Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(it.id)} data-testid={`bilan-delete-${it.id}`} className="rounded-xl h-10 w-10 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
